import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { fetchMe, postLogout } from "@/lib/api";
import { identifyUser, resetIdentity } from "@/lib/analytics-boot";

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
  setUser: () => {},
  showSignIn: (_returnPath) => {},
  hideSignIn: () => {},
  signInOpen: false,
  signInReturnPath: "/",
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInReturnPath, setSignInReturnPath] = useState("/");

  const refresh = useCallback(async () => {
    try {
      const u = await fetchMe();
      setUser(u);
      if (u) identifyUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await postLogout(); } catch { /* ignore */ }
    setUser(null);
    resetIdentity();
  }, []);

  const showSignIn = useCallback((returnPath) => {
    setSignInReturnPath(returnPath || (window.location.pathname + window.location.search));
    setSignInOpen(true);
  }, []);
  const hideSignIn = useCallback(() => setSignInOpen(false), []);

  return (
    <AuthContext.Provider
      value={{ user, loading, refresh, logout, setUser, showSignIn, hideSignIn, signInOpen, signInReturnPath }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function startGoogleLogin(returnPath = "/") {
  const redirectUrl = window.location.origin + returnPath;
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
}

export function useAuth() {
  return useContext(AuthContext);
}
